import { useState, useRef, useEffect } from 'react';
import { HeaderWrapper, Layout, TableCell, TableLayout } from 'components/Hospital/PaginationForms';
import { getCLManagingPatientsTableConfig } from 'constants/pagination';
import { NothingDataInManagingPatientsPagination } from 'pages/Admin/DCK/AddDiagnosisPage/NothingDataInPagination';
import { DiagnosisListPaginationProps } from 'models/Hospital/paginationTypes';
import ArrowLineUp from 'assets/Icons/Arrow/ArrowLineUp';
import { OtherSortingButton } from 'styles/Global/buttonStyles';
import ArrowLineDown from 'assets/Icons/Arrow/ArrowLineDown';
import { ICLUserInfo } from 'models/CL/CLBasicInterface';
import CLManagingPatientsPaginationTable from './CLManagingPatientsPaginationTable';
import QueryCustomedPagination from 'components/Pagination/QueryCustomedPagination';
import styled from 'styled-components';

export interface CLManagingPatientsPaginationProps extends Omit<DiagnosisListPaginationProps, 'currentData'> {
    type?: string;
    currentData?: ICLUserInfo[];
    showPagination?: boolean;
    selectedPatients?: string[];
    onPatientSelect?: (patientId: string) => void;
    showFavoriteButton?: boolean;
    refetchUserListData?: () => void;
    refetchFavoriteUserListData?: () => void;
}

const CLManagingPatientsPagination = ({
    type,
    currentPage,
    totalPages,
    pageGroup,
    handlePageGroup,
    handlePageChange,
    currentData,
    initNumber,
    handleSorting,
    sortField,
    sortDirections,
    handleResetButton,
    showPagination = true,
    selectedPatients = [],
    onPatientSelect,
    showFavoriteButton = false,
    refetchUserListData,
    refetchFavoriteUserListData,
}: CLManagingPatientsPaginationProps) => {
    // Add state and ref for scroll shadow
    const [showShadow, setShowShadow] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        // Check if scroll is possible and data exists
        const checkScrollable = () => {
            if (!currentData || currentData.length === 0) {
                setShowShadow(false);
            } else {
                setShowShadow(el.scrollHeight > el.clientHeight);
            }
        };
        checkScrollable();
        window.addEventListener('resize', checkScrollable);
        return () => {
            window.removeEventListener('resize', checkScrollable);
        };
    }, [currentData]);

    const { headers: tableHeaders, widths: tableWidths } = getCLManagingPatientsTableConfig(showFavoriteButton);
    const arrowColumnIndex = tableHeaders.length - 1;

    const tableHeader = (
        <HeaderWrapper $showShadow={showShadow}>
            {tableHeaders.map((name, idx) => {
                const isRegistration = name.type === 'registrationTimestamp';
                return (
                    <TableCell
                        key={idx}
                        $idx={idx}
                        $width={tableWidths[idx]}
                        $type='header'
                        $justifyContent='space-between'
                        // hover={setSelectHeader}
                        cellType={name.type}
                        $maxWidth={idx === arrowColumnIndex ? '60px' : undefined}
                        $show='always'
                    >
                        {name.title}
                        {isRegistration && (
                            <OtherSortingButton onClick={() => handleSorting('registrationTimestamp')}>
                                {sortField === 'registrationTimestamp' && sortDirections['registrationTimestamp'] ? <ArrowLineUp /> : <ArrowLineDown />}
                            </OtherSortingButton>
                        )}
                    </TableCell>
                );
            })}
        </HeaderWrapper>
    );

    const tableBody =
        !currentData || currentData?.length === 0 ? (
            <NothingDataInManagingPatientsPagination initNumber={initNumber} handleResetButton={handleResetButton} type={type} />
        ) : (
            <CLManagingPatientsPaginationTable
                data={currentData}
                selectedPatients={selectedPatients}
                onPatientSelect={onPatientSelect}
                showFavoriteButton={showFavoriteButton}
                refetchUserListData={refetchUserListData}
                refetchFavoriteUserListData={refetchFavoriteUserListData}
            />
        );

    return (
        <Layout>
            {showPagination ? (
                <TableLayout>
                    {tableHeader}
                    {tableBody}
                </TableLayout>
            ) : (
                <TableLayout>
                    {tableHeader}
                    <ScrollableTableWrapper ref={scrollRef}>{tableBody}</ScrollableTableWrapper>
                </TableLayout>
            )}
            {showPagination && currentData && totalPages !== 0 && (
                <QueryCustomedPagination currentPage={currentPage} onChangeCurrentPage={handlePageChange} sectionIndex={pageGroup} totalPages={totalPages} />
            )}
        </Layout>
    );
};

export default CLManagingPatientsPagination;

const ScrollableTableWrapper = styled.div`
    height: 348px;
    overflow-y: auto;
    width: 100%;
`;
